function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { renderNotAvailablePriceUI, getVariablePriceStringWithDefault } from '@shared/utils/pricing';
import { SortedTable, SortedTH, SortOrder } from './sortedTable';
import { Plan } from '@shared/utils/constants';
import { SimplePriceCell } from './simplePriceCell';
import FuturePriceWarning from '@shared/components/futurePriceWarning';
import PureSpzaComponent from '@shared/components/pureSpzaComponent';
import pdpPrivateBadge from '@shared/images/pdp-privateBadge.svg';
export class SimplePlanPricing extends PureSpzaComponent {
    handleSortChange(sortColumnName, order) {
        this.setState({
            sortConfig: {
                sortColumnName,
                sortOrder: order
            }
        });
    }
    showColumnByUnit(skus, unit) {
        return skus.some((sku)=>sku.termPrices && sku.termPrices.some((termPrice)=>termPrice.unit.toLowerCase() === unit));
    }
    getBadge(sku) {
        if (sku.isPrivate) {
            return /*#__PURE__*/ _jsx("img", {
                className: "banner",
                src: pdpPrivateBadge
            });
        }
    }
    getPriceColumnsData(skus, renderBadges) {
        const { futurePrices, isOneTimePaymentOffer } = this.props;
        const showPriceColumn = skus.some((sku)=>!!sku.startingPrice);
        const showYearlyPrice = this.showColumnByUnit(skus, 'year');
        let showMonthlyPrice = this.showColumnByUnit(skus, 'month');
        // If the offer is Microsoft managed and without prices.
        if (this.props.isMicrosoftManaged && skus.length && !showYearlyPrice && !showMonthlyPrice) {
            showMonthlyPrice = true;
        }
        const formattedSkus = skus.map((sku)=>{
            var _sku_startingPrice;
            const futureSku = futurePrices === null || futurePrices === void 0 ? void 0 : futurePrices.skus.find((futurePriceSku)=>futurePriceSku.id === sku.id);
            const { monthly, yearly, price, futurePriceMonthly, futurePriceYearly } = getVariablePriceStringWithDefault({
                sku,
                showPriceColumn,
                showYearlyPrice,
                showMonthlyPrice,
                isMicrosoftManaged: this.props.isMicrosoftManaged,
                countryCode: this.props.billingCountry.countryCode,
                currency: this.props.billingCountry.currency,
                context: this.context,
                futureSku,
                isOneTimePaymentOffer,
                locale: this.props.locale,
                shouldBlockPrices: this.props.shouldBlockPrices
            });
            let row = {
                title: sku.title,
                description: sku.description,
                displayRank: sku.displayRank,
                monthly: monthly && /*#__PURE__*/ _jsx(SimplePriceCell, {
                    price: monthly,
                    futurePrice: futurePriceMonthly
                }),
                yearly: yearly && /*#__PURE__*/ _jsx(SimplePriceCell, {
                    price: yearly,
                    futurePrice: futurePriceYearly
                }),
                price: ((_sku_startingPrice = sku.startingPrice) === null || _sku_startingPrice === void 0 ? void 0 : _sku_startingPrice.meterId) ? `${price}/${sku.startingPrice.meterId}` : price,
                priceInternal: showPriceColumn && sku.startingPrice ? sku.startingPrice.value : 0,
                rowClassName: 'pricingListItem',
                rowKey: sku.title
            };
            if (renderBadges) {
                row = _object_spread({
                    badge: renderBadges && this.getBadge(sku) || ' '
                }, row);
            }
            return row;
        });
        const showGeneralPrice = showPriceColumn && !showMonthlyPrice && !showYearlyPrice;
        return {
            showMonthlyPrice,
            showYearlyPrice,
            formattedSkus,
            showGeneralPrice
        };
    }
    renderImpl() {
        const { pricing, isOneTimePaymentOffer } = this.props;
        if (!pricing) {
            return /*#__PURE__*/ _jsx("div", {
                children: this.context.loc('Loading', 'Loading...')
            });
        }
        const skus = pricing.skus;
        const hasBadges = skus.some((sku)=>sku.isPrivate === true);
        const { showMonthlyPrice, showYearlyPrice, showGeneralPrice, formattedSkus } = this.getPriceColumnsData(skus, hasBadges);
        return skus.length === 0 ? renderNotAvailablePriceUI(this.context) : /*#__PURE__*/ _jsx("div", {
            className: "pricing simplePlanPricing",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "c-table f-divided",
                children: [
                    /*#__PURE__*/ _jsx(FuturePriceWarning, {
                        futurePrices: this.props.futurePrices
                    }),
                    /*#__PURE__*/ _jsx(SortedTable, {
                        thead: [
                            /*#__PURE__*/ _jsxs("tr", {
                                className: "pricingListItem",
                                children: [
                                    hasBadges && /*#__PURE__*/ _jsx("th", {
                                        className: "f-sortable",
                                        colSpan: 1,
                                        "aria-sort": "none",
                                        scope: "col"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "f-sortable",
                                        colSpan: 1,
                                        "aria-sort": "none",
                                        scope: "col",
                                        children: this.context.loc('Plan', 'Plan')
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "f-sortable",
                                        colSpan: 1,
                                        "aria-sort": "none",
                                        scope: "col",
                                        children: this.context.loc('Description', 'Description')
                                    }),
                                    showMonthlyPrice ? /*#__PURE__*/ _jsx(SortedTH, {
                                        boundProperty: "priceInternal",
                                        handleSortChange: this.handleSortChange,
                                        currentlySelected: this.state.sortConfig.sortColumnName,
                                        children: isOneTimePaymentOffer ? this.context.loc('Price', 'Price') : this.context.loc('Monthly_Price', 'Monthly Price')
                                    }) : null,
                                    showYearlyPrice ? /*#__PURE__*/ _jsx(SortedTH, {
                                        boundProperty: "priceInternal",
                                        handleSortChange: this.handleSortChange,
                                        currentlySelected: this.state.sortConfig.sortColumnName,
                                        children: this.context.loc('Annual_Price', 'Annual Price')
                                    }) : null,
                                    showGeneralPrice ? /*#__PURE__*/ _jsx(SortedTH, {
                                        boundProperty: "priceInternal",
                                        handleSortChange: this.handleSortChange,
                                        currentlySelected: this.state.sortConfig.sortColumnName,
                                        children: this.context.loc('Price', 'Price')
                                    }) : null
                                ]
                            }, "0")
                        ],
                        tbody: formattedSkus,
                        sortConfig: this.state.sortConfig,
                        ignore: [
                            'rowKey',
                            'rowClassName',
                            'priceInternal',
                            Plan.displayRank
                        ],
                        setInnerHTMLColumns: [
                            'description'
                        ],
                        multiLineColumns: [
                            'monthly',
                            'yearly',
                            'title'
                        ]
                    })
                ]
            })
        });
    }
    constructor(props, context){
        super(props, context), _define_property(this, "context", void 0);
        this.state = {
            sortConfig: {
                sortColumnName: Plan.displayRank,
                sortOrder: SortOrder.Ascending
            }
        };
        this.handleSortChange = this.handleSortChange.bind(this);
    }
}
SimplePlanPricing.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func,
    buildHref: PropTypes.func,
    ctaCallback: PropTypes.func,
    renderErrorModal: PropTypes.func
};
